import type { ReactNode } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";

type SettingsActionTone = "default" | "primary" | "danger" | "muted";

const ACTION_TONE: Record<SettingsActionTone, string> = {
  default:
    "border-border/40 text-foreground/82 hover:border-brand/30 hover:bg-slate-900/[0.025] dark:hover:bg-white/[0.025] hover:text-foreground",
  primary:
    "border-success/20 bg-success/[0.055] text-success hover:border-success/[0.36] hover:bg-success/[0.095] hover:text-success",
  danger:
    "border-danger/20 bg-danger/[0.055] text-danger hover:border-danger/[0.36] hover:bg-danger/[0.095] hover:text-danger",
  muted:
    "border-border/40 text-muted-foreground hover:border-border/40 hover:bg-slate-900/[0.022] dark:hover:bg-white/[0.022] hover:text-foreground/88",
};

export function SettingsActionRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mt-5 flex flex-col gap-2 border-t border-border/40 pt-4 sm:flex-row sm:flex-wrap sm:items-center",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SettingsActionButton({
  icon,
  tone = "default",
  className,
  children,
  ...props
}: ButtonProps & {
  icon?: IconName;
  tone?: SettingsActionTone;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      className={cn(
        "h-9 w-full rounded-full bg-transparent px-3.5 text-sm font-semibold shadow-none transition-colors focus-visible:ring-0 focus-visible:ring-offset-0 sm:w-auto",
        ACTION_TONE[tone],
        className,
      )}
      {...props}
    >
      {icon ? <Icon name={icon} className="h-3.5 w-3.5" /> : null}
      {children}
    </Button>
  );
}
